/* oxlint-disable eslint/max-lines -- pre-existing popover; this change is scrollbar-subtle only */
/**
 * SourceControlPopoverV2 Component (EVI-721)
 *
 * Source control popover:
 * 1. Clinical web (toggle + optional detail panel) — pro only
 * 2. Literature and guidelines (toggle) — pro only, when clinical-web panel FF is OFF
 * 3. My library (submenu) — pro only
 * 4. Partner sources (submenu) — pro only
 *
 * `release-source-control-clinical-web-panel` gates the clinical-web drill-in
 * (guidelines move into that panel). When the flag is OFF, keep the Literature
 * and guidelines row at the root.
 *
 * `release-source-control-nested-sources` gates the nested-sources redesign of
 * the non-panel path: Clinical web shows a Globe icon, Literature and guidelines
 * nests under it in an IndentRail, and a separator divides the clinical block
 * from the secondary sources. When OFF, the flat layout with the Stethoscope
 * icon is restored.
 *
 * Uses exact same atoms as V1 — no separate logic stack.
 */

import { TierProFilled } from '@heidi/design-system/icons';
import { healthPathwaysSetupDialogOpenAtom } from '@/atoms/healthPathwaysAtoms';
import { openSourceControlPopoverAtom, toggleWebSearchAtom } from '@/atoms/sourceControlAtoms';
import { isSoleEnabledChatSourceBucket } from '@/atoms/chatSourceRequireOne';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { usePrimarySourcesDefaultInit } from '@/components/chat/hooks/usePrimarySourcesDefaultInit';
import { Button } from '@heidi/design-system/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@heidi/design-system/popover';
import { Separator } from '@heidi/design-system/separator';
import { Switch } from '@heidi/design-system/switch';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { IS_UNIFIED_COLLECTIONS_ENABLED } from '@/features/flags/legacy-flags';
import { HealthPathwaysSetupDialog } from '@/composition/hosts/evidence/healthpathways-setup-dialog';
import {
  CreateCollectionWizard,
  UnifiedCreateCollectionModal,
} from '@heidi/evidence';
import { LastSourceRequiredTooltip } from '@/components/chat/components/source-control-v2/LastSourceRequiredTooltip';
import { useEvidenceBillingInfo } from '@/hooks/useEvidenceBillingInfo';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { cn } from '@/lib/cn';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { EvidenceSignUpWall } from '@/models/analytics/features/evidence';
import { ButtonAction } from '@heidi/runtime/analytics-vocabulary';
import type { TranslationKey } from '@heidi/runtime/i18n';
import { useTranslations } from '@heidi/runtime/i18n';
import { useAtomValue, useSetAtom } from 'jotai';
import { BookOpen, ChevronRight, Globe, Stethoscope } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useIntl } from 'react-intl';
import { IndentRail } from '@heidi/design-system/indent-rail';
import {
  useExpandedSourceControlTrackingEnabled,
  useIsSourceControlClinicalWebPanelEnabled,
  useIsSourceControlNestedSourcesEnabled,
} from '@/features/flags';
import { ClinicalToolsSubmenu } from './components/clinical-tools-submenu';
import { ClinicalWebPanel } from './components/clinical-web-panel';
import { MyLibrarySubmenu } from './components/my-library-submenu';
import { SourceControlPopoverV2Banners } from './components/source-control-popover-v2-banners';
import { SourceRecommendationPopoverBanner } from './components/source-recommendation-popover-banner';
import { useTeamEvidenceEnforcement } from '@/hooks/useTeamEvidenceEnforcement';
import { useSourceControlActions } from './hooks/useSourceControlActions';
import { track } from '@/lib/analytics/track';

interface SourceControlPopoverV2Props {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'end';
  tooltipContent?: string;
  showApplyChangesButton?: boolean;
  onApplyChangesClick?: () => void;
  // Work Home compact composer only: keep focus off this row so opening or
  // closing Sources does not collapse engagement or ring the trigger. Other
  // call sites must keep Radix autofocus so keyboard / AT users can Tab into
  // the portalled panel.
  suppressOpenAutoFocus?: boolean;
}

export const SourceControlPopoverV2 = ({
  children,
  open: controlledOpen,
  onOpenChange,
  side = 'top',
  align = 'start',
  tooltipContent,
  showApplyChangesButton = false,
  onApplyChangesClick,
  suppressOpenAutoFocus = false,
}: SourceControlPopoverV2Props) => {
  const { t } = useTranslations();
  const intl = useIntl();
  const [internalOpen, setInternalOpen] = useState(false);
  const [isCreateWizardOpen, setIsCreateWizardOpen] = useState(false);
  const [popoverView, setPopoverView] = useState<'root' | 'clinicalWeb'>('root');

  const isOpen = controlledOpen ?? internalOpen;
  const { baseProps } = useEvidenceAnalytics();
  const {
    toggleState,
    handleClinicalSourcesToggle,
    handleDocCollectionToggle,
    handleWebCollectionToggle,
    handleUnifiedCollectionToggle,
    isRequireAtLeastOneSource,
  } = useSourceControlActions(baseProps);
  const toggleWebSearch = useSetAtom(toggleWebSearchAtom);
  const { hasEvidencePro } = useEvidenceBillingInfo();

  const { isAuthenticated } = useIsAuthenticated();

  const {
    isEnforced,
    sourceControl: teamSourceControl,
    teamName,
    canCreateTeamCollection,
  } = useTeamEvidenceEnforcement();
  // The wizard's team-scoping facts, resolved here: `useTeamEvidenceEnforcement`
  // is `src/hooks/`, which is not a product glob, so Chat may read it.
  const wizardTeamContext = useMemo(
    () => ({ canCreateTeamCollection, teamName }),
    [canCreateTeamCollection, teamName]
  );

  // Initialize defaults (runs once per session)
  usePrimarySourcesDefaultInit();

  const isExpandedSourceTrackingEnabled = useExpandedSourceControlTrackingEnabled();
  const isClinicalWebPanelEnabled = useIsSourceControlClinicalWebPanelEnabled();
  const isNestedSourcesEnabled = useIsSourceControlNestedSourcesEnabled();
  // "Clinical sources" row is on when either guidelines or primary evidence is on.
  // (Toggle still writes both via handleClinicalSourcesToggle.)
  const isClinicalSourcesOn =
    toggleState.localGuidelines || toggleState.primaryEvidence;
  const showClinicalWeb =
    !(isEnforced && !teamSourceControl.webCollections.enabled);
  const showClinicalSources =
    !isClinicalWebPanelEnabled &&
    !(isEnforced && !teamSourceControl.clinicalSources.enabled);
  const showLibrary =
    !(isEnforced && !teamSourceControl.library.enabled);
  const showPartnerSources =
    !(isEnforced && !teamSourceControl.integrationPartners.enabled);
  const showSourceSectionSeparator =
    isNestedSourcesEnabled &&
    !isClinicalWebPanelEnabled &&
    (showClinicalWeb || showClinicalSources) &&
    (showLibrary || showPartnerSources);

  const lockLastWebSearch =
    isRequireAtLeastOneSource &&
    isSoleEnabledChatSourceBucket(toggleState, 'webSearch');
  const lockLastClinicalSources =
    isRequireAtLeastOneSource &&
    isSoleEnabledChatSourceBucket(toggleState, 'clinicalSources');

  const openTriggerRef = useRef<'manual' | 'auto'>('manual');
  const openFromAtom = useAtomValue(openSourceControlPopoverAtom);
  useEffect(() => {
    if (openFromAtom) {
      openTriggerRef.current = 'auto';
    }
  }, [openFromAtom]);

  useEffect(() => {
    if (!isClinicalWebPanelEnabled && popoverView === 'clinicalWeb') {
      setPopoverView('root');
    }
  }, [isClinicalWebPanelEnabled, popoverView]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setInternalOpen(open);
      onOpenChange?.(open);
      if (!open) {
        setPopoverView('root');
      }

      if (!isExpandedSourceTrackingEnabled) return;

      const activeFiltersCount = [
        toggleState.webSearch,
        toggleState.localGuidelines,
        toggleState.primaryEvidence,
        toggleState.myLibrary,
        toggleState.webCollections,
      ].filter(Boolean).length;

      const trigger = openTriggerRef.current;
      openTriggerRef.current = 'manual';

      track({
        event_name: open
          ? TrackingEventName.PanelOpened
          : TrackingEventName.PanelClosed,
        props: {
          ...baseProps,
          panel_type: 'source_control',
          trigger,
          feature: 'evidence',
          active_filters_count: activeFiltersCount,
        },
      });
    },
    [
      onOpenChange,
      isExpandedSourceTrackingEnabled,
      baseProps,
      toggleState.webSearch,
      toggleState.localGuidelines,
      toggleState.primaryEvidence,
      toggleState.myLibrary,
      toggleState.webCollections,
    ]
  );

  const handleApplyChangesClick = useCallback(() => {
    onApplyChangesClick?.();
    handleOpenChange(false);
  }, [onApplyChangesClick, handleOpenChange]);

  const handleCreateCollectionClick = useCallback(() => {
    track({ options: { amplitude: false },
      event_name: TrackingEventName.EvidenceSignUpWallViewed,
      props: { wall_type: EvidenceSignUpWall.SourceSelectionWall },
    });
    handleOpenChange(false);
    setIsCreateWizardOpen(true);
  }, [handleOpenChange]);

  // Close the panel as setup opens, the same order `handleCreateCollectionClick`
  // uses: the dialog is mounted beside `Popover` rather than inside it, so the
  // panel closing no longer takes the dialog with it.
  //
  // `isPartnerSetupMounted` is what keeps that mount SINGULAR. This component has
  // four call sites (the two composer buttons, the mobile drawer, Work's link
  // menu) and Evidence settings renders the same dialog, all driven by one atom —
  // so mounting unconditionally would stack overlays and focus traps wherever two
  // are alive at once. Only the instance the user actually clicked mounts it, the
  // same way `isCreateWizardOpen` gates the collection modals below.
  const [isPartnerSetupMounted, setIsPartnerSetupMounted] = useState(false);
  const isPartnerSetupOpen = useAtomValue(healthPathwaysSetupDialogOpenAtom);

  const handleOpenPartnerSetup = useCallback(() => {
    setIsPartnerSetupMounted(true);
    handleOpenChange(false);
  }, [handleOpenChange]);

  // Release the mount once the dialog closes itself, so a later open from
  // settings is not answered by this instance as well.
  useEffect(() => {
    if (!isPartnerSetupOpen) setIsPartnerSetupMounted(false);
  }, [isPartnerSetupOpen]);

  const handleCollectionCreated = useCallback(
    (collection: { id: string; type: 'documents' | 'web' }) => {
      if (IS_UNIFIED_COLLECTIONS_ENABLED()) {
        // Unified mode: all newly created collections go through the unified toggle.
        // 'library' is a temporary default bucket; handleUnifiedCollectionToggle checks
        // both buckets atomically on deselect, so the choice is safe.
        handleUnifiedCollectionToggle(collection.id, 'library');
      } else if (collection.type === 'documents') {
        handleDocCollectionToggle(collection.id);
      } else {
        handleWebCollectionToggle(collection.id);
      }
    },
    [handleDocCollectionToggle, handleWebCollectionToggle, handleUnifiedCollectionToggle]
  );

  const triggerElement = tooltipContent ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <PopoverTrigger asChild>{children}</PopoverTrigger>
      </TooltipTrigger>
      <TooltipContent side="top" hidden={isOpen}>
        {tooltipContent}
        <TooltipArrow />
      </TooltipContent>
    </Tooltip>
  ) : (
    <PopoverTrigger asChild>{children}</PopoverTrigger>
  );

  const clinicalSourcesRow = showClinicalSources ? (
    <SourceToggleRow
      isActive={isClinicalSourcesOn}
      onToggle={(value) => {
        if (!isAuthenticated) return;
        handleClinicalSourcesToggle(value);
        if (isExpandedSourceTrackingEnabled) {
          track({
            event_name: TrackingEventName.ButtonClicked,
            props: {
              ...baseProps,
              action: ButtonAction.EvidenceLocalGuidelinesClicked,
              label: 'Literature and guidelines',
              is_enabled: value,
              feature: 'evidence',
            },
          });
        }
      }}
      icon={<BookOpen size={16} className="text-text-primary" />}
      label={t('sources.sourceControl.clinicalSources' as TranslationKey)}
      description={t('sources.sourceControl.clinicalSourcesDescription' as TranslationKey)}
      secondaryNote={intl.formatMessage({
        defaultMessage: 'Searches may take longer when this is on.',
        id: 'U3meJmBV1o',
      })}
      ariaLabel="Toggle literature and guidelines"
      disabled={!hasEvidencePro || !isAuthenticated}
      proLocked={!hasEvidencePro}
      teamLocked={!!(isEnforced && !teamSourceControl.clinicalSources.changeable)}
      lastSourceLocked={lockLastClinicalSources}
      teamName={teamName}
    />
  ) : null;

  return (
    <>
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        {triggerElement}
        <PopoverContent
          side={side}
          align={align}
          sideOffset={8}
          // Keeps the panel off the viewport edge, as the nested submenus do.
          collisionPadding={16}
          // A viewport too short for the panel on either side would clip the
          // rows that fall outside it — scroll the corpus instead.
          className="scrollbar-subtle max-h-[var(--radix-popover-content-available-height)] min-w-64 max-w-[calc(100vw-2rem)] gap-0 overflow-y-auto p-0 sm:min-w-96"
          data-testid="source-control-popover-content-v2"
          onOpenAutoFocus={
            suppressOpenAutoFocus ? (e) => e.preventDefault() : undefined
          }
          onCloseAutoFocus={
            suppressOpenAutoFocus ? (e) => e.preventDefault() : undefined
          }
        >
          <div className="flex flex-col gap-1 p-2">
            <SourceRecommendationPopoverBanner onClose={() => handleOpenChange(false)} />

            {isClinicalWebPanelEnabled && popoverView === 'clinicalWeb' ? (
              <ClinicalWebPanel
                onBack={() => setPopoverView('root')}
                hasEvidencePro={hasEvidencePro}
                isAuthenticated={isAuthenticated}
                guidelinesTeamDisabled={
                  !!(isEnforced && !teamSourceControl.clinicalSources.enabled)
                }
                guidelinesTeamLocked={
                  !!(isEnforced && !teamSourceControl.clinicalSources.changeable)
                }
                teamName={teamName}
              />
            ) : (
              <>
                {showClinicalWeb && (
                  <ClinicalWebRow
                    icon={
                      isNestedSourcesEnabled && !isClinicalWebPanelEnabled ? (
                        <Globe size={16} className="text-text-primary" />
                      ) : (
                        <Stethoscope size={16} className="text-text-primary" />
                      )
                    }
                    isActive={toggleState.webSearch && isAuthenticated}
                    onToggle={(value) => {
                      if (!isAuthenticated) return;
                      toggleWebSearch(value);
                      if (isExpandedSourceTrackingEnabled) {
                        track({
                          event_name: TrackingEventName.ButtonClicked,
                          props: {
                            ...baseProps,
                            action: ButtonAction.EvidenceWebSearchClicked,
                            label: 'Clinical web',
                            is_enabled: value,
                            feature: 'evidence',
                          },
                        });
                      }
                    }}
                    onNavigate={
                      isClinicalWebPanelEnabled
                        ? () => setPopoverView('clinicalWeb')
                        : undefined
                    }
                    label={t('sources.sourceControl.webSearch' as TranslationKey)}
                    description={t('sources.sourceControl.webSearchDescription' as TranslationKey)}
                    disabled={!hasEvidencePro || !isAuthenticated}
                    proLocked={!hasEvidencePro}
                    teamLocked={!!(isEnforced && !teamSourceControl.webCollections.changeable)}
                    lastSourceLocked={lockLastWebSearch}
                    teamName={teamName}
                  />
                )}

                {clinicalSourcesRow ? (
                  isNestedSourcesEnabled && showClinicalWeb ? (
                    <IndentRail
                      className="ms-5"
                      data-testid="clinical-web-literature-rail"
                    >
                      {clinicalSourcesRow}
                    </IndentRail>
                  ) : (
                    clinicalSourcesRow
                  )
                ) : null}

                {showSourceSectionSeparator ? (
                  <Separator
                    className="my-1"
                    data-testid="source-control-section-separator"
                  />
                ) : null}

                {showLibrary && (
                  <div className="flex items-center">
                    <div className="min-w-0 flex-1">
                      {/* An enforced library stays navigable: the submenu locks
                          each enforced collection individually and sanitizeSourceConfig
                          re-pins them at search time, so gating the row here would only
                          hide the Optional collections members are meant to toggle. */}
                      <MyLibrarySubmenu
                        disabled={!hasEvidencePro}
                        onCreateCollectionClick={handleCreateCollectionClick}
                      />
                    </div>
                    {!hasEvidencePro && (
                      <div className="mr-2 flex items-center">
                        <TierProFilled className="size-3 text-tier-pro" />
                      </div>
                    )}
                  </div>
                )}

                {showPartnerSources && (
                  <div className="flex items-center">
                    <div className="min-w-0 flex-1">
                      <ClinicalToolsSubmenu
                        disabled={!hasEvidencePro || !!(isEnforced && !teamSourceControl.integrationPartners.changeable)}
                        onOpenPartnerSetup={handleOpenPartnerSetup}
                      />
                    </div>
                    {!hasEvidencePro && (
                      <div className="mr-2 flex items-center">
                        <TierProFilled className="size-3 text-tier-pro" />
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Apply changes button */}
          {showApplyChangesButton && hasEvidencePro && (
            <div className="px-4 pb-4 pt-2.5">
              <Button
                type="button"
                variant="default"
                size="md"
                className="w-full"
                onClick={handleApplyChangesClick}
              >
                {t('sources.useTheseSources')}
              </Button>
            </div>
          )}

          <SourceControlPopoverV2Banners onClose={() => handleOpenChange(false)} isTeamEnforced={isEnforced} />
        </PopoverContent>
      </Popover>

      {/* AGE-1490: mounted OUTSIDE `Popover`, like the collection modals below.
          Inside `PopoverContent` it shared the panel's lifetime, and on Work the
          add-menu's pointer watch closes this panel ~200ms after the cursor moves
          onto a flyout it does not recognise. A Dialog portal is not a popper
          wrapper, so reaching for the postcode field closed the panel and
          unmounted the dialog with it. */}
      {isPartnerSetupMounted && <HealthPathwaysSetupDialog />}

      {/* Create Collection Wizard (replaces UploadDocumentModal + CreateCollectionModal) */}
      {IS_UNIFIED_COLLECTIONS_ENABLED() ? (
        <UnifiedCreateCollectionModal
          open={isCreateWizardOpen}
          onOpenChange={setIsCreateWizardOpen}
          onCollectionCreated={handleCollectionCreated}
          teamContext={wizardTeamContext}
        />
      ) : (
        <CreateCollectionWizard
          open={isCreateWizardOpen}
          onOpenChange={setIsCreateWizardOpen}
          initialStep="entry"
          onCollectionCreated={handleCollectionCreated}
          teamContext={wizardTeamContext}
        />
      )}
    </>
  );
};

const ClinicalWebRow = ({
  icon,
  isActive,
  onToggle,
  onNavigate,
  label,
  description,
  disabled,
  proLocked,
  teamLocked,
  lastSourceLocked = false,
  teamName,
}: {
  icon: ReactNode;
  isActive: boolean;
  onToggle: (value: boolean) => void;
  onNavigate?: () => void;
  label: string;
  description: string;
  disabled: boolean;
  proLocked: boolean;
  teamLocked: boolean;
  lastSourceLocked?: boolean;
  teamName?: string;
}) => {
  const allowNavigate = onNavigate != null;
  const teamLockTooltip = teamLocked
    ? `Required by ${teamName ?? 'your team'}`
    : undefined;
  const showLastSourceTooltip = lastSourceLocked && !teamLocked && !proLocked;
  const switchDisabled = disabled || teamLocked || lastSourceLocked;

  const handleNavigate = () => {
    if (disabled || !allowNavigate) return;
    onNavigate();
  };

  // Wrap Switch in a span when tooltiping: TooltipTrigger asChild must not
  // land on Switch itself (Radix data-state collision hides checked styles).
  const switchEl = (
    <Switch
      checked={isActive}
      onCheckedChange={onToggle}
      disabled={switchDisabled}
      size="xs"
      aria-label="Toggle clinical web"
      onClick={(event) => event.stopPropagation()}
    />
  );

  const wrappedSwitch = showLastSourceTooltip ? (
    <LastSourceRequiredTooltip>{switchEl}</LastSourceRequiredTooltip>
  ) : teamLockTooltip ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex shrink-0">{switchEl}</span>
      </TooltipTrigger>
      <TooltipContent side="top">{teamLockTooltip}</TooltipContent>
    </Tooltip>
  ) : (
    switchEl
  );

  const labelContent = (
    <>
      <div className="flex items-center gap-2">
        <div className="flex size-4 items-center justify-center">{icon}</div>
        <TypographyP2 className="font-normal text-text-primary">{label}</TypographyP2>
      </div>
      <div className="pl-6">
        <TypographyCaption className="text-text-tertiary">{description}</TypographyCaption>
      </div>
    </>
  );

  return (
    <div
      className={cn(
        'flex items-start justify-between gap-2 rounded-md p-2 transition-colors',
        !disabled && allowNavigate && 'hover:bg-surface-hover'
      )}
    >
      {allowNavigate ? (
        <button
          type="button"
          onClick={handleNavigate}
          disabled={disabled}
          className={cn(
            'flex min-w-0 flex-1 flex-col gap-1 rounded-md text-left',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          )}
          aria-label={`Open ${label} settings`}
        >
          {labelContent}
        </button>
      ) : (
        <div className="flex min-w-0 flex-1 flex-col gap-1">{labelContent}</div>
      )}
      <div className="flex shrink-0 items-center gap-1 pt-0.5">
        {proLocked ? <TierProFilled className="size-3 text-tier-pro" /> : null}
        {wrappedSwitch}
        {allowNavigate ? (
          <button
            type="button"
            onClick={handleNavigate}
            disabled={disabled}
            className={cn(
              'flex size-6 items-center justify-center rounded-md',
              disabled && 'cursor-not-allowed'
            )}
            aria-label={`Open ${label} settings`}
          >
            <ChevronRight size={16} className="text-text-secondary rtl:-scale-x-100" />
          </button>
        ) : null}
      </div>
    </div>
  );
};

const SourceToggleRow = ({
  isActive,
  onToggle,
  icon,
  label,
  description,
  secondaryNote,
  ariaLabel,
  disabled,
  proLocked,
  teamLocked,
  lastSourceLocked = false,
  teamName,
}: {
  isActive: boolean;
  onToggle: (value: boolean) => void;
  icon: ReactNode;
  label: string;
  description: string;
  secondaryNote?: string;
  ariaLabel: string;
  disabled: boolean;
  proLocked: boolean;
  teamLocked: boolean;
  lastSourceLocked?: boolean;
  teamName?: string;
}) => {
  const tooltipText = teamLocked
    ? `Required by ${teamName ?? 'your team'}`
    : undefined;
  const showLastSourceTooltip = lastSourceLocked && !teamLocked && !proLocked;
  const switchDisabled = disabled || teamLocked || lastSourceLocked;

  // Wrap Switch in a span when tooltiping: TooltipTrigger asChild must not
  // land on Switch itself (Radix data-state collision hides checked styles).
  const switchEl = (
    <Switch
      checked={isActive}
      onCheckedChange={onToggle}
      disabled={switchDisabled}
      size="xs"
      aria-label={ariaLabel}
    />
  );

  return (
    <div
      className="flex items-start justify-between gap-2 p-2"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-2">
          <div className="flex size-4 items-center justify-center">{icon}</div>
          <TypographyP2 className="font-normal text-text-primary">{label}</TypographyP2>
        </div>
        <div className="flex flex-col gap-1 pl-6">
          <TypographyCaption className="text-text-tertiary">
            {description}
          </TypographyCaption>
          {secondaryNote ? (
            <TypographyCaption className="text-text-tertiary">
              {secondaryNote}
            </TypographyCaption>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1 pt-0.5">
        {proLocked ? <TierProFilled className="size-3 text-tier-pro" /> : null}
        {showLastSourceTooltip ? (
          <LastSourceRequiredTooltip>{switchEl}</LastSourceRequiredTooltip>
        ) : tooltipText ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex shrink-0">{switchEl}</span>
            </TooltipTrigger>
            <TooltipContent side="top">{tooltipText}</TooltipContent>
          </Tooltip>
        ) : (
          switchEl
        )}
      </div>
    </div>
  );
};
